import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { portalConst } from '@app/config';
import { AlertService } from '@app/core/services';
import { AttachmentService } from '@app/core/services/attachment.service';
import { fileSvg, notNull, safeEmpty } from '@app/shared/utils';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Subscription } from 'rxjs';
import { ModalFileViewerComponent } from '../modal-file-viewer/modal-file-viewer.component';
import { UploadFile, UploadStatus } from './dropzone.types';
import { TypeFile } from '@app/core/models';

export interface ExistingAttachment {
  id: string | number;
  name: string;
  size?: number;
  url: string;
  mimeType?: string;
  isExisting?: boolean;
}

@Component({
  selector: 'app-attachment-dropzone',
  imports: [CommonModule, AngularSvgIconModule, TranslateModule],
  templateUrl: './attachment-dropzone.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentDropzoneComponent {
  @Input() public accept = '*/*';
  @Input() public maxSizeMB = 50;
  @Input() public multiple = true;
  @Input() public multipartThresholdMB = 100;
  @Input() public maxFiles = 0;
  @Input() public folder = 'geral';
  @Input() public disabled = false;
  @Input() public downloadable = false;
  @Input() public previewable = false;

  @Output() public uploadComplete = new EventEmitter<UploadFile>();
  @Output() public uploadError = new EventEmitter<UploadFile>();
  @Output() public filesChanged = new EventEmitter<UploadFile[]>();
  @Output() public removeExisting = new EventEmitter<string>();

  private readonly _attachmentService = inject(AttachmentService);
  private readonly _translateService = inject(TranslateService);
  private readonly _alertService = inject(AlertService);
  private readonly _dialog = inject(Dialog);

  private _uploadSubs = new Map<string, { abort: () => void }>();
  private _importedAttachmentIds = new Set<string>();

  protected isDragOver = signal(false);
  protected files = signal<UploadFile[]>([]);

  protected totalFiles = computed(() => this.files().length);
  protected doneFiles = computed(() => this.files().filter((f) => f.status === 'done').length);
  protected errorFiles = computed(() => this.files().filter((f) => f.status === 'error').length);
  protected uploadingFiles = computed(() => this.files().filter((f) => f.status === 'uploading').length);

  @Input() public set initialAttachments(attachments: ExistingAttachment[]) {
    if (!attachments?.length) {
      return;
    }

    let hasNew = false;
    const mapped: UploadFile[] = [];

    for (const att of attachments) {
      const idStr = String(att.id);
      if (!this._importedAttachmentIds.has(idStr)) {
        this._importedAttachmentIds.add(idStr);
        hasNew = true;
        mapped.push({
          id: idStr,
          file: null as File,
          name: att.name,
          size: att.size ?? 0,
          mimeType: att.mimeType ?? '',
          status: 'done' as UploadStatus,
          progress: 100,
          key: att.url,
          errorMessage: null,
          canRetry: false,
          isExisting: true,
        });
      }
    }

    if (hasNew) {
      setTimeout(() => {
        this.files.update((prev) => [...prev, ...mapped]);
        this.filesChanged.emit(this.files());
      });
    }
  }

  @HostListener('dragover', ['$event'])
  protected onDragOver(e: DragEvent) {
    e.preventDefault();
    this.isDragOver.set(true);
  }

  @HostListener('dragleave', ['$event'])
  protected onDragLeave(e: DragEvent) {
    if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) {
      this.isDragOver.set(false);
    }
  }

  @HostListener('drop', ['$event'])
  protected onDrop(e: DragEvent) {
    e.preventDefault();
    this.isDragOver.set(false);
    this.processFiles(Array.from(e.dataTransfer?.files ?? []));
  }

  protected onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    this.processFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  protected onDownloadFile(file, event: Event): void {
    event.stopPropagation();

    if (file.uploadedFile) {
      this.downloadFile(`${portalConst.aws.s3Snv}/${file.key}`, file.name);
      return;
    }

    this._attachmentService
      .getPresignedDownloadUrl(file.id)
      .pipe(safeEmpty())
      .subscribe({
        next: ({ download_url }) => {
          this.downloadFile(download_url, file.name);
        },
        error: () => this._alertService.alert({ message: 'geral.erro-download' }),
      });
  }

  protected onPreviewFile(file, event: Event): void {
    event.stopPropagation();

    let key;

    if (file.uploadedFile) {
      key = `${portalConst.aws.s3Snv}/${file.key}`;
    } else {
      key = file.key;
    }
    const formattedFile = this.formatFile(file);

    this._dialog.open(ModalFileViewerComponent, {
      data: formattedFile,
    });
  }

  protected onRemoveFile(id: string, event: Event) {
    event.stopPropagation();
    const file = this.files().find((f) => f.id === id);

    if (file?.isExisting) {
      this._alertService
        .confirm({ message: 'eclass.dropzone.confirmar-remover-arquivo' })
        .pipe(notNull())
        .subscribe(() => {
          this.files.update((prev) => prev.filter((f) => f.id !== id));
          this.filesChanged.emit(this.files());
          this.removeExisting.emit(id);
        });
      return;
    }

    if (this._uploadSubs.has(id)) {
      this._uploadSubs.get(id)!.abort();
      this._uploadSubs.delete(id);
    }

    this.files.update((prev) => prev.filter((f) => f.id !== id));
    this.filesChanged.emit(this.files());
    this.removeExisting.emit(id);
  }

  protected onRetryFile(id: string, event: Event) {
    event.stopPropagation();
    const entry = this.getFile(id);
    if (entry) {
      this.updateFile(id, { status: 'waiting', progress: 0, errorMessage: undefined });
      this.startUpload(entry);
    }
  }

  protected clearAll() {
    this._uploadSubs.forEach((sub) => sub.abort());
    this._uploadSubs.clear();
    this.files.set([]);
    this._importedAttachmentIds.clear();
    this.filesChanged.emit([]);
  }

  protected formatSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 ** 2) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }

  protected getFileIcon(name: string): string {
    if (!name) {
      return fileSvg('');
    }
    const ext = name.split('.').pop()?.toLowerCase() ?? '';
    return fileSvg(ext);
  }

  private checkFileType(file: File): boolean {
    if (this.accept === '*/*') {
      return true;
    }
    const acceptList = this.accept.split(',').map((a) => a.trim().toLowerCase());
    const fileExt = '.' + (file.name.split('.').pop()?.toLowerCase() ?? '');
    const fileMime = file.type.toLowerCase();

    return acceptList.some((accepted) => {
      if (accepted.startsWith('.')) {
        return fileExt === accepted;
      }
      if (accepted.endsWith('/*')) {
        return fileMime.startsWith(accepted.replace('/*', ''));
      }
      return fileMime === accepted;
    });
  }

  private processFiles(rawFiles: File[]) {
    const maxBytes = this.maxSizeMB * 1024 * 1024;

    let entries: UploadFile[] = rawFiles.map((f) => {
      const isSizeValid = f.size <= maxBytes;
      const isTypeValid = this.checkFileType(f);

      let status: UploadStatus = 'waiting';
      let errorMessage: string | undefined;

      let canRetry = true;

      if (!isSizeValid) {
        status = 'error';
        canRetry = false;
        errorMessage = this._translateService.instant('eclass.dropzone.tamanho-excede-limite', {
          size: this.maxSizeMB,
        });
      } else if (!isTypeValid) {
        status = 'error';
        canRetry = false;
        errorMessage = this._translateService.instant('eclass.dropzone.formato-nao-permitido');
      }

      return {
        id: crypto.randomUUID(),
        file: f,
        name: f.name,
        size: f.size,
        progress: 0,
        status,
        errorMessage,
        canRetry,
      };
    });

    if (this.maxFiles > 0) {
      const remainingSlots = Math.max(0, this.maxFiles - this.files().length);
      entries = entries.slice(0, remainingSlots);
    }

    this.files.update((prev) => [...prev, ...entries]);
    this.filesChanged.emit(this.files());
    entries.forEach((e) => {
      if (e.status !== 'error') {
        this.startUpload(e);
      } else {
        this.uploadError.emit(e);
      }
    });
  }

  private startUpload(entry: UploadFile) {
    const threshold = this.multipartThresholdMB * 1024 * 1024;
    if (entry.file.size > threshold) {
      this.multipartUpload(entry);
    } else {
      this.simpleUpload(entry);
    }
  }

  private simpleUpload(entry: UploadFile) {
    this.updateFile(entry.id, { status: 'uploading' });
    const sub = this._attachmentService.simpleUpload(entry.file, this.folder).subscribe({
      next: (event) => {
        if ('progress' in event) {
          this.updateFile(entry.id, { progress: event.progress });
        } else {
          this._uploadSubs.delete(entry.id);
          this.updateFile(entry.id, { status: 'done', progress: 100, key: event.key, uploadedFile: event.file });
          this.uploadComplete.emit(this.getFile(entry.id)!);
        }
      },
      error: (err) => {
        this._uploadSubs.delete(entry.id);
        this.handleError(entry.id, err);
      },
    });

    this._uploadSubs.set(entry.id, { abort: () => sub.unsubscribe() });
  }

  private async multipartUpload(entry: UploadFile) {
    const PART_SIZE = 5 * 1024 * 1024;
    const totalParts = Math.ceil(entry.file.size / PART_SIZE);

    this.updateFile(entry.id, { status: 'uploading' });

    const abortController = new AbortController();
    let isAborted = false;
    let completeSub: Subscription | undefined;

    this._uploadSubs.set(entry.id, {
      abort: () => {
        isAborted = true;
        abortController.abort();
        initSub?.unsubscribe();
        completeSub?.unsubscribe();
      },
    });

    const initSub: Subscription | undefined = this._attachmentService
      .initMultipartUpload(entry.file.name, entry.file.type, this.folder, totalParts)
      .pipe(safeEmpty())
      .subscribe({
        next: async ({ upload_id, key, parts, file }) => {
          try {
            if (isAborted) {
              throw new DOMException('Aborted', 'AbortError');
            }
            let completed = 0;
            const uploadedParts = await Promise.all(
              parts.map(async ({ part_number, upload_url }) => {
                const start = (part_number - 1) * PART_SIZE;
                const blob = entry.file.slice(start, start + PART_SIZE);
                const etag = await this._attachmentService.uploadPart(upload_url, blob, abortController.signal);
                if (isAborted) {
                  throw new DOMException('Aborted', 'AbortError');
                }
                this.updateFile(entry.id, { progress: Math.round((++completed / totalParts) * 100) });
                return { part_number, etag };
              }),
            );

            if (isAborted) {
              throw new DOMException('Aborted', 'AbortError');
            }

            completeSub = this._attachmentService.completeMultipartUpload(key, upload_id, uploadedParts).subscribe({
              next: () => {
                this._uploadSubs.delete(entry.id);
                this.updateFile(entry.id, { status: 'done', progress: 100, key, uploadedFile: file });
                this.uploadComplete.emit(this.getFile(entry.id)!);
              },
              error: (err) => {
                this._uploadSubs.delete(entry.id);
                this.handleError(entry.id, err);
              },
            });
          } catch (err: any) {
            this._uploadSubs.delete(entry.id);
            if (err?.name === 'AbortError') {
              this._attachmentService.abortMultipartUpload(key, upload_id).subscribe();
              return;
            }
            this._attachmentService.abortMultipartUpload(key, upload_id).subscribe();
            this.handleError(entry.id, err);
          }
        },
        error: (err) => {
          this._uploadSubs.delete(entry.id);
          this.handleError(entry.id, err);
        },
      });
  }

  private updateFile(id: string, patch: Partial<UploadFile>) {
    this.files.update((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
    this.filesChanged.emit(this.files());
  }

  private getFile(id: string) {
    return this.files().find((f) => f.id === id);
  }

  private async downloadFile(url: string, title: string): Promise<void> {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = title;
    a.click();

    URL.revokeObjectURL(blobUrl);
  }

  private handleError(id: string, err: any) {
    this.updateFile(id, {
      status: 'error',
      errorMessage: err?.message ?? this._translateService.instant('eclass.dropzone.erro-enviar'),
    });
    this.uploadError.emit(this.getFile(id)!);
  }

  private formatFile(file: any): File {
    const formattedFile: any = {};
    formattedFile.title = file.name;
    formattedFile.mimetype = file.mimetype;
    formattedFile.url = file.uploadedFile ? `${portalConst.aws.s3Snv}/${file.key}` : file.key;
    const url = file.uploadedFile ? `${portalConst.aws.s3Snv}/${file.key}` : file.key;

    switch (file.extensao) {
      case 'xlsx':
      case 'pptx':
      case 'docx':
        formattedFile.type = TypeFile.Office;
        formattedFile.file = url;
        break;
      case 'png':
      case 'jpg':
      case 'jpeg':
        formattedFile.type = TypeFile.Image;
        formattedFile.file = url;
        break;
      case 'pdf':
        formattedFile.type = TypeFile.Pdf;
        formattedFile.file = url;
        break;
      case 'mp4':
      case 'mov':
      case 'm4v':
        formattedFile.type = TypeFile.Video;
        formattedFile.file = url;
        break;
      case 'mp3':
        formattedFile.type = TypeFile.Audio;
        formattedFile.file = url;
        break;
    }

    return formattedFile;
  }
}
